import { getLocalizedItem, getLocalizedItemsByKind, getRecipeForDish, getRecipeIngredients } from "@/lib/data";
import type { ItemKind } from "@/lib/data";
import { defaultLocale, type Locale } from "@/lib/i18n/config";
import {
  getIngredientTagName,
  getIngredientTagImageUrl,
  getIngredientTagPrice,
  TAG_FALLBACK_KIND,
} from "@/lib/ingredient-tags";

export interface IngredientOption {
  id: string;
  kind: ItemKind;
  name: string;
  icon: string | null;
}

export interface IngredientDetail {
  key: string;
  name: string;
  nameFallback: boolean;
  qty: number;
  icon: string | null;
  unitPrice: number | null;
  totalPrice: number | null;
  hasPrice: boolean;
  isTag: boolean;
  itemId: string | null;
  itemKind: ItemKind | null;
  mixOptionItemIds: string[] | null;
  /** Items offered as substitutes for this "any" slot — curated (from Heartodex) when
   * mixOptionItemIds is set, otherwise a best-effort "every item of this kind" fallback for a
   * handful of tags that map cleanly to one ItemKind. Null when neither is available. */
  expandOptions: IngredientOption[] | null;
  /** True when expandOptions came from a real Heartodex-confirmed list, false when it's the
   * "every item of this kind" fallback (which may be broader than what the game actually accepts). */
  expandIsCurated: boolean;
}

export interface RecipeCardData {
  id: string;
  name: string;
  nameFallback: boolean;
  sellPrice: number;
  /** null when no source records one — 266 of 404 recipes. Never default it to a number: the
   *  catalog page prints it as "Lv. N" and `isBeginnerFriendly` gates an editorial callout. */
  cookingLevel: number | null;
  tool: string;
  image: string | null;
  imageStatus: string;
  ingredients: IngredientDetail[];
  knownCost: number;
  profit: number | null;
  /** Low unlock level (<=5, matching the Cooking hobby's early unlock band) plus a known,
   * positive profit — the "safe to recommend to a brand-new cook" signal used by the Best
   * Recipes to Start With section on the catalog page and the badge on the detail page. Recipes
   * with unknown ingredient data (profit === null) are never flagged, even if cookingLevel
   * qualifies — an unverified recipe isn't a "good pick", it's a data gap. */
  isBeginnerFriendly: boolean;
}

const BEGINNER_COOKING_LEVEL_CAP = 5;

function buildExpandOptions(
  ing: { ingredientTag?: string | null; mixOptionItemIds?: string[] | null },
  locale: Locale,
): { expandOptions: IngredientOption[] | null; expandIsCurated: boolean } {
  if (ing.mixOptionItemIds && ing.mixOptionItemIds.length > 0) {
    const options = ing.mixOptionItemIds
      .map((id) => getLocalizedItem(id, locale))
      .filter((item): item is NonNullable<typeof item> => !!item)
      .map((item) => ({
        id: item.id,
        kind: item.kind,
        name: item.displayName,
        icon: item.imageStatus === "verified" ? (item.image ?? null) : null,
      }));
    return { expandOptions: options, expandIsCurated: true };
  }
  const fallbackKind = ing.ingredientTag ? TAG_FALLBACK_KIND[ing.ingredientTag] : undefined;
  if (fallbackKind) {
    const options = getLocalizedItemsByKind(fallbackKind as ItemKind, locale).map((item) => ({
      id: item.id,
      kind: item.kind,
      name: item.displayName,
      icon: item.imageStatus === "verified" ? (item.image ?? null) : null,
    }));
    return { expandOptions: options, expandIsCurated: false };
  }
  return { expandOptions: null, expandIsCurated: false };
}

function buildIngredientDetails(recipeId: string, locale: Locale): IngredientDetail[] {
  return getRecipeIngredients(recipeId).map((ing, idx) => {
    const refItem = ing.itemId ? getLocalizedItem(ing.itemId, locale) : undefined;
    const name = refItem?.displayName ?? getIngredientTagName(ing.ingredientTag);
    const icon = refItem?.imageStatus === "verified" ? refItem.image : getIngredientTagImageUrl(ing.ingredientTag);
    const unitPrice = getIngredientTagPrice(ing.ingredientTag) ?? refItem?.sellPrice ?? null;
    const hasPrice = unitPrice !== null;
    const { expandOptions, expandIsCurated } = buildExpandOptions(ing, locale);
    return {
      key: `${ing.itemId ?? ing.ingredientTag ?? "?"}-${idx}`,
      name,
      nameFallback: refItem?.isNameFallback ?? false,
      qty: ing.qty,
      icon: icon ?? null,
      unitPrice,
      totalPrice: hasPrice ? unitPrice! * ing.qty : null,
      hasPrice,
      isTag: !!ing.ingredientTag,
      itemId: refItem?.id ?? null,
      itemKind: refItem?.kind ?? null,
      mixOptionItemIds: ing.mixOptionItemIds ?? null,
      expandOptions,
      expandIsCurated,
    };
  });
}

export function buildRecipeCardData(locale: Locale = defaultLocale): RecipeCardData[] {
  return getLocalizedItemsByKind("dish", locale).map((item) => {
    const recipe = getRecipeForDish(item.id);
    const ingredients = recipe ? buildIngredientDetails(recipe.id, locale) : [];
    const knownCost = ingredients.reduce((sum, i) => sum + (i.totalPrice ?? 0), 0);
    // With zero ingredients recorded, knownCost is 0 by construction — that's "we don't know
    // the cost", not "the cost is free". Treating it as free silently inflated profit to equal
    // the full sell price, which is worse than just not showing a number.
    const profit = item.sellPrice != null && ingredients.length > 0 ? item.sellPrice - knownCost : null;
    const cookingLevel = recipe?.cookingLevel ?? null;
    return {
      id: item.id,
      name: item.displayName,
      nameFallback: item.isNameFallback,
      sellPrice: item.sellPrice ?? 0,
      cookingLevel,
      tool: recipe?.tool ?? "NONE",
      image: item.image ?? null,
      imageStatus: item.imageStatus,
      ingredients,
      knownCost,
      profit,
      isBeginnerFriendly:
        cookingLevel != null && cookingLevel <= BEGINNER_COOKING_LEVEL_CAP && profit != null && profit > 0,
    };
  });
}
